<template>
  <div style="padding: 20px">
    <!-- ==================== 说明 + 操作栏 ==================== -->
    <el-alert
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 16px"
        title="合同模板 = 「换合同不用改代码」的唯一入口">
      <div>
        新增一种合同只需三步：① 把做好的合同 PDF 传到 MinIO/服务器 → ② 填本页 PDF 地址并点「识别表单域」自动生成字段定义 →
        ③ 配好落章关键字与电子印章（留空则用系统全局配置）。之后在「合同签署管理」页面即可直接发起。
      </div>
      <div style="margin-top: 4px; color: #909399">
        本模块走的是<b>法大大老版（v2）接口</b>，不需要「法大大签署模板ID」，PDF 由后端直接上传并在我方一侧自动盖章。
      </div>
    </el-alert>

    <div class="filter-bar">
      <el-input
          v-model="keyword"
          placeholder="按名称 / 编码筛选"
          clearable
          style="width: 220px; margin-right: 10px">
      </el-input>
      <el-button type="primary" @click="getData">刷新</el-button>
      <el-button type="success" @click="openAdd">+ 新增合同模板</el-button>
    </div>

    <!-- ==================== 模板列表 ==================== -->
    <el-table
        v-loading="loading"
        :data="filteredTemplates"
        border
        :cell-style="{ padding: '10px' }"
        header-cell-class-name="table-header">
      <el-table-column prop="templateName" label="模板名称" min-width="180" show-overflow-tooltip></el-table-column>
      <el-table-column prop="templateCode" label="模板编码" width="170" show-overflow-tooltip></el-table-column>
      <el-table-column prop="bizType" label="业务分类" width="100" show-overflow-tooltip></el-table-column>
      <el-table-column label="我方角色" width="160">
        <template #default="scope">
          <el-tag :type="scope.row.ourRole === 'partyA' ? 'warning' : 'info'" size="small">
            {{ scope.row.ourRoleText || '我方为乙方' }}
          </el-tag>
          <div class="mini">我方落「{{ scope.row.ourRole === 'partyA' ? '甲方' : '乙方' }}栏」</div>
        </template>
      </el-table-column>
      <el-table-column label="PDF 来源" min-width="220" show-overflow-tooltip>
        <template #default="scope">
          <span v-if="scope.row.pdfUrl">{{ scope.row.pdfUrl }}</span>
          <el-tag v-else type="danger" size="small">未配置</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="落章关键字（按栏位）" width="180">
        <template #default="scope">
          <div class="mini">
            甲栏：{{ scope.row.effectiveSignKeywordA || '-' }}
            <el-tag v-if="scope.row.ourRole === 'partyA'" size="mini" type="warning">我方用</el-tag>
            <el-tag v-else size="mini">对方用</el-tag>
          </div>
          <div class="mini">
            乙栏：{{ scope.row.effectiveSignKeywordB || '-' }}
            <el-tag v-if="scope.row.ourRole === 'partyA'" size="mini">对方用</el-tag>
            <el-tag v-else size="mini" type="warning">我方用</el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="电子印章" width="180" show-overflow-tooltip>
        <template #default="scope">
          <div>{{ scope.row.effectiveCorpSignatureId || '未配置' }}</div>
          <div class="mini">{{ scope.row.corpSignatureId ? '（本模板专用）' : '（继承系统全局）' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="表单字段" width="90" align="center">
        <template #default="scope">{{ fieldCount(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="启用" width="80" align="center">
        <template #default="scope">
          <el-tag :type="scope.row.state === 1 ? 'success' : 'info'" size="small">
            {{ scope.row.state === 1 ? '启用' : '停用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="就绪" width="90" align="center">
        <template #default="scope">
          <el-tooltip v-if="scope.row.ready" content="可直接发起签署" placement="top">
            <el-tag type="success" size="small" effect="dark">已就绪</el-tag>
          </el-tooltip>
          <el-tooltip v-else :content="scope.row.unreadyReason" placement="top">
            <el-tag type="warning" size="small" effect="dark">未就绪</el-tag>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="290" fixed="right" align="center">
        <template #default="scope">
          <el-button size="small" type="text" @click="openEdit(scope.row)">编辑</el-button>
          <el-button size="small" type="text" @click="doDetectFor(scope.row)">识别表单域</el-button>
          <el-button size="small" type="text" @click="doPrecheck(scope.row)">预检</el-button>
          <el-button size="small" type="text" class="op-danger" @click="doDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- ==================== 新增 / 编辑弹窗 ==================== -->
    <el-dialog
        :title="form.id ? ('编辑合同模板：' + form.templateCode) : '新增合同模板'"
        v-model="editDialog"
        width="1080px"
        :close-on-click-modal="false"
        top="4vh">
      <div class="edit-body">
        <el-form :model="form" label-width="120px" size="default">
          <el-divider content-position="left">① 基本信息</el-divider>
          <el-row :gutter="12">
            <el-col :span="12">
              <el-form-item label="模板编码" required>
                <el-input v-model="form.templateCode" placeholder="大写字母+下划线，如 CONSULT_EMPOWER"
                          :disabled="!!form.id"></el-input>
                <div class="field-tip">业务唯一标识，建好后一般不要改（历史合同按它关联）</div>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="模板名称" required>
                <el-input v-model="form.templateName" placeholder="展示用，如 企业咨询服务合同-焱火落地赋能"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="12">
            <el-col :span="12">
              <el-form-item label="业务分类">
                <el-input v-model="form.bizType" placeholder="如 咨询合同 / 合作协议 / 采购合同"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="签署顺序">
                <el-select v-model="form.signRule" style="width: 100%">
                  <el-option label="留空 = 我方先（推荐，随角色自适应）" value=""></el-option>
                  <el-option label="B_FIRST（乙方栏先签）" value="B_FIRST"></el-option>
                  <el-option label="A_FIRST（甲方栏先签）" value="A_FIRST"></el-option>
                  <el-option label="PARALLEL（并行，同时发链接）" value="PARALLEL"></el-option>
                </el-select>
                <div class="field-tip">
                  <b style="color: #409eff">{{ signRuleTip }}</b><br/>
                  按 PDF 上的「甲方/乙方栏」表述。我方有自动章时章是即时的；仅当「先签方=我方且我方需人工手写」时，
                  才等「我方」签完再发对方链接。留空默认「我方先」。
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">② 合同 PDF（换合同只换这里）</el-divider>
          <el-form-item label="合同 PDF" required>
            <el-upload
                :http-request="uploadTemplatePdf"
                :show-file-list="false"
                :before-upload="beforePdfUpload"
                accept=".pdf,application/pdf">
              <el-button :loading="uploadingPdf" icon="Upload">上传 PDF 到 MinIO</el-button>
            </el-upload>
            <el-input v-model="form.pdfUrl" placeholder="上传后自动填入 MinIO 地址；也可手动粘贴已有 http(s) 链接" style="margin-top:8px">
              <template #append>
                <el-button :loading="detecting" @click="doDetectInForm">识别表单域</el-button>
              </template>
            </el-input>
            <div class="field-tip">
              优先点「上传 PDF 到 MinIO」选本地 PDF，系统自动传到对象存储并回填地址；也可直接粘贴已有链接（服务器内网/外网均可）。
            </div>
          </el-form-item>
          <el-form-item label="PDF 文件名">
            <el-input v-model="form.pdfName" placeholder="选填，如 企业咨询服务合同---焱火落地赋能_表单版.pdf"></el-input>
          </el-form-item>

          <el-alert
              v-if="detectResult"
              type="success"
              :closable="false"
              show-icon
              style="margin-bottom: 16px"
              :title="'PDF 体检：' + detectResult.pages + ' 页 / ' + (detectResult.fields || []).length + ' 个表单域'">
            <div v-if="detectResult.keywords">
              落章关键字命中：
              <span v-for="(v, k) in detectResult.keywords" :key="k" style="margin-right: 12px">
                「{{ k }}」<b :style="{color: v > 0 ? '#67c23a' : '#f56c6c'}">{{ v }}</b> 次
              </span>
            </div>
            <div v-if="detectSummary" style="margin-top: 4px">{{ detectSummary }}</div>
            <div style="margin-top: 4px; color: #909399">{{ detectResult.keywordStrategyTip }}</div>
          </el-alert>

          <el-divider content-position="left">③ 身份与角色（先定「谁是我方」）</el-divider>
          <el-alert type="info" :closable="false" show-icon style="margin-bottom: 14px"
                    :title="roleSummaryTitle">
            <div v-html="roleSummaryHtml"></div>
          </el-alert>
          <el-row :gutter="12">
            <el-col :span="8">
              <el-form-item label="我方角色">
                <el-select v-model="form.ourRole" style="width: 100%">
                  <el-option label="我方为乙方（默认）" value="partyB"></el-option>
                  <el-option label="我方为甲方" value="partyA"></el-option>
                </el-select>
                <div class="field-tip">
                  只决定我方章 / 手写落<b>「甲方栏」还是「乙方栏」</b>，
                  <b>不改变</b>下面任何字段的归属含义。
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="甲方栏称谓">
                <el-input v-model="form.partyATitle" placeholder="默认 甲方"></el-input>
                <div class="field-tip">仅 PDF / 文案上显示的称呼，不代表签署方身份</div>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="乙方栏称谓">
                <el-input v-model="form.partyBTitle" placeholder="默认 乙方"></el-input>
                <div class="field-tip">仅 PDF / 文案上显示的称呼，不代表签署方身份</div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="12">
            <el-col :span="16">
              <el-form-item :label="ourRoleLabel + '公司名称'">
                <el-input v-model="form.partyBName" placeholder="留空则用系统配置的我方公司全称"></el-input>
                <div class="field-tip">
                  填的<b>恒为「我方（焱火）」的公司全称</b>（字段名 party_b_name 是历史命名，勿按字面理解）。
                  留空时取 yml <code>fdd.contract.partyBName</code>。
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="8">
              <el-form-item label="合同有效期(天)">
                <el-input-number v-model="form.validDays" :min="0" :max="3650"
                                 controls-position="right" style="width: 100%"></el-input-number>
                <div class="field-tip">发布时设定；0 = 长期有效。逾期未签完自动置「已过期」</div>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left">④ 签署要素与落章配置</el-divider>
          <el-alert type="warning" :closable="false" show-icon style="margin-bottom: 14px"
                    title="字段归属速查（与 PDF 上的「甲乙」栏位无关）">
            <div>
              带<b>「我方」</b>字样的一律指<b>焱火自己</b>；带<b>「对方」</b>字样的一律指<b>交易对手</b>。<br/>
              唯一例外是<b>落章关键字</b>——它按 <b>PDF 物理栏位</b>命名（甲方栏 / 乙方栏），
              谁用取决于上面的「我方角色」。
            </div>
          </el-alert>

          <el-divider content-position="left" style="margin: 6px 0 14px">
            ④-1 我方（焱火）签署要素 —— 恒指我们，选甲方选乙方都不变
          </el-divider>
          <el-row :gutter="12">
            <el-col :span="10">
              <el-form-item :label="'我方签署要素（落 ' + mySideLabel + '栏）'">
                <el-select v-model="form.partyBAutoSign" style="width: 100%">
                  <el-option label="仅自动盖章（推荐，我方零人工）" :value="1"></el-option>
                  <el-option label="仅授权负责人手写签字" :value="0"></el-option>
                  <el-option label="盖章 + 授权负责人手写签字（最完整）" :value="2"></el-option>
                </el-select>
                <div class="field-tip">
                  <b>恒定指我方（焱火）</b>，字段名 party_b_auto_sign 是历史命名。<br/>
                  选「仅手写 / 盖章+手写」且「签署顺序 = 我方先」时，发起后先出<b>我方手写链接</b>，
                  我方签完才发对方链接。
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="14">
              <el-form-item label="我方电子印章ID">
                <el-input v-model="form.corpSignatureId"
                          placeholder="留空 = 用系统全局 fdd.contract.corpSignatureId"></el-input>
                <div class="field-tip">
                  <b>恒指我方（焱火）的章</b>（字段名 corp_signature_id）。
                  到法大大后台「印章管理」查看（我们的公章 ID 是 1789043792396520）。
                  同一主体盖不同的章、或不同法人主体合同时，才需要在这里单独填。
                  <b>本地 PNG 印章图不能用于线上签署。</b>
                </div>
              </el-form-item>
            </el-col>
          </el-row>

          <el-divider content-position="left" style="margin: 6px 0 14px">
            ④-2 对方（交易对手）签署要素 —— 恒指交易对手，与「我方角色」无关
          </el-divider>
          <el-row :gutter="12">
            <el-col :span="8">
              <el-form-item label="对方企业盖章">
                <el-switch v-model="form.partyASeal" :active-value="1" :inactive-value="0"></el-switch>
                <div class="field-tip">
                  仅对方是<b>企业</b>时开启。开启后由对方<b>在签署链接里用自己的法大大账号自助加盖企业章</b>
                  —— 我方<b>无需</b>知道对方印章ID，也<b>无需</b>对方额外授权。<br/>
                  <span style="color:#909399">
                    前提：对方须已完成法大大企业实名；未实名时对方打开链接会看到「尚未实名」提示。
                  </span>
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="16">
              <el-form-item label="对方企业印章ID（可选）">
                <el-input v-model="form.partyASealSignatureId" :disabled="form.partyASeal !== 1"
                          placeholder="留空即可（推荐）—— 由对方自助盖章；仅当对方已把印章授权给我方代盖时才填"></el-input>
                <div class="field-tip">
                  字段名 party_a_seal_signature_id。<b>留空（推荐）</b>＝ 对方在签署链接里自助盖章；
                  <b>填写</b>＝ 我方用该印章代盖（extsign_auto，需对方已把此章授权给我方，极少用）。<br/>
                  该ID只存在于对方自己的法大大账号里，我方一般拿不到 —— <b>不必强求</b>。
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-form-item label="对方授权负责人手写签字">
            <el-switch v-model="form.partyAHand" :active-value="1" :inactive-value="0"></el-switch>
            <span style="margin-left: 10px; color: #909399; font-size: 12px">
              字段名 party_a_hand
            </span>
            <div class="field-tip">
              <b>默认开启</b>。<b>对方是自然人时强制手写</b>（没有公章可用，此开关不生效）；
              对方是企业时可关掉（例如只用企业章、不需要负责人签字）。
            </div>
          </el-form-item>
          <el-alert v-if="!form.partyASeal && !form.partyAHand" type="error" :closable="false" show-icon
                    style="margin-bottom: 16px"
                    title="对方没有任何签署要素，合同无法生效">
            <div>硬性规则：<b>每一方至少要有「盖章」或「手写签字」其中之一</b>。
              这里同时关掉了「对方企业盖章」和「对方授权负责人手写签字」，对方在合同上将无任何签署要素。</div>
          </el-alert>

          <el-divider content-position="left" style="margin: 6px 0 14px">
            ④-3 落章关键字（按 PDF 物理栏位命名，非按签署方）
          </el-divider>
          <el-row :gutter="12">
            <el-col :span="12">
              <el-form-item label="『甲方栏』落章关键字">
                <el-input v-model="form.signKeywordA" placeholder="留空 = 甲方："></el-input>
                <div class="field-tip">
                  PDF 上<b>甲方栏</b>的定位文字。按当前「我方角色」，<b>{{ kwAUserText }}</b>。
                </div>
              </el-form-item>
            </el-col>
            <el-col :span="12">
              <el-form-item label="『乙方栏』落章关键字">
                <el-input v-model="form.signKeywordB" placeholder="留空 = 乙方："></el-input>
                <div class="field-tip">
                  PDF 上<b>乙方栏</b>的定位文字。按当前「我方角色」，<b>{{ kwBUserText }}</b>。
                </div>
              </el-form-item>
            </el-col>
          </el-row>
          <el-alert
              type="warning"
              :closable="false"
              show-icon
              style="margin-bottom: 16px"
              title="关键字必须出现在 PDF 正文里，且出现次数决定落章位置">
            <div>出现 2 次以上（如抬头 + 签章区）时，系统全局的 keywordStrategy 必须为 2（只签最后一个），
              否则会在两处各盖一次章。可点上面的「识别表单域」直接看命中次数。</div>
          </el-alert>

          <el-divider content-position="left">⑤ 表单内容定义（决定发起时出现哪些输入框 + 写入 PDF 哪些域）</el-divider>
          <div class="field-tip" style="margin: -4px 0 10px">
            <b>key 必须是 PDF 里真实的表单域名（不是中文标签）</b>，否则值写不进去。
            点「识别表单域」可自动生成 / 补齐；已有同名字段会保留你配置的标签与必填设置。
          </div>
          <div class="row-tools">
            <el-button size="small" @click="addFieldRow">+ 添加字段</el-button>
            <el-button size="small" @click="openJsonDialog">查看 / 编辑 JSON</el-button>
            <span class="mini" style="margin-left: 8px">共 {{ form.fields.length }} 项</span>
          </div>
          <div class="field-table-wrap">
            <el-table :data="form.fields" border size="small" :row-class-name="fieldRowClass">
              <el-table-column label="PDF 域名字(key)" width="190">
                <template #default="scope">
                  <el-input v-model="scope.row.key" size="small" placeholder="txtComA"></el-input>
                </template>
              </el-table-column>
              <el-table-column label="显示标签" width="180">
                <template #default="scope">
                  <el-input v-model="scope.row.label" size="small" placeholder="甲方公司名称"></el-input>
                </template>
              </el-table-column>
              <el-table-column label="类型" width="110">
                <template #default="scope">
                  <el-select v-model="scope.row.type" size="small" style="width: 100%">
                    <el-option label="文本" value="text"></el-option>
                    <el-option label="数字" value="number"></el-option>
                    <el-option label="多行文本" value="textarea"></el-option>
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column label="必填" width="70" align="center">
                <template #default="scope">
                  <el-switch v-model="scope.row.required"></el-switch>
                </template>
              </el-table-column>
              <el-table-column label="自动带出" width="150">
                <template #default="scope">
                  <el-select v-model="scope.row.defaultFrom" size="small" clearable placeholder="不自动带出"
                             style="width: 100%">
                    <el-option v-for="o in defaultFromOptions" :key="o.value" :label="o.label" :value="o.value"></el-option>
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column label="固定默认值" min-width="130">
                <template #default="scope">
                  <el-input v-model="scope.row.default" size="small" placeholder="选填"></el-input>
                </template>
              </el-table-column>
              <el-table-column label="PDF 校验" width="90" align="center">
                <template #default="scope">
                  <el-tag v-if="scope.row.inPdf === true" type="success" size="small">命中</el-tag>
                  <el-tag v-else-if="scope.row.inPdf === false" type="danger" size="small">不存在</el-tag>
                  <span v-else class="mini">-</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="70" align="center">
                <template #default="scope">
                  <el-button size="small" type="text" class="op-danger"
                             @click="form.fields.splice(scope.$index, 1)">删</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <el-divider content-position="left">⑤-2 图片表单域（PDF 识别的图片域 → 发起时按数量出上传位）</el-divider>
          <div class="field-tip" style="margin: -4px 0 10px">
            <b>PDF 里有几个图片表单域，发起时就出现几个上传位</b>（身份证 / 学历 / 学籍 / 银行卡 / 一寸免冠照等由识别自动判定，
            不再写死「身份证 2 张 + 银行卡 2 张」）。点上方「识别表单域」自动填充；也可人工增删。
          </div>
          <div class="row-tools">
            <el-button size="small" @click="addImageRow">+ 添加图片域</el-button>
            <span class="mini" style="margin-left: 8px">共 {{ (form.imageFields || []).length }} 项</span>
          </div>
          <div class="field-table-wrap">
            <el-table :data="form.imageFields" border size="small">
              <el-table-column label="PDF 域名字(name)" width="200">
                <template #default="scope">
                  <el-input v-model="scope.row.name" size="small" placeholder="IdCardImage1"></el-input>
                </template>
              </el-table-column>
              <el-table-column label="显示标签" width="190">
                <template #default="scope">
                  <el-input v-model="scope.row.label" size="small" placeholder="身份证正面"></el-input>
                </template>
              </el-table-column>
              <el-table-column label="适配" width="140">
                <template #default="scope">
                  <el-select v-model="scope.row.fit" size="small" style="width: 100%">
                    <el-option label="contain(等比)" value="contain"></el-option>
                    <el-option label="fill(拉伸填满)" value="fill"></el-option>
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column label="必传" width="70" align="center">
                <template #default="scope">
                  <el-switch v-model="scope.row.required"></el-switch>
                </template>
              </el-table-column>
              <el-table-column label="落图坐标(自动)" min-width="150">
                <template #default="scope">
                  <span class="mini" v-if="scope.row.page !== undefined && scope.row.page !== null">
                    第{{ Number(scope.row.page) + 1 }}页 x{{ scope.row.x }} y{{ scope.row.y }} w{{ scope.row.w }} h{{ scope.row.h }}
                  </span>
                  <span class="mini" v-else>按占位域自动取矩形</span>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="70" align="center">
                <template #default="scope">
                  <el-button size="small" type="text" class="op-danger"
                             @click="form.imageFields.splice(scope.$index, 1)">删</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <el-divider content-position="left">⑥ 状态与备注</el-divider>
          <el-row :gutter="12">
            <el-col :span="8">
              <el-form-item label="启用">
                <el-switch v-model="form.state" :active-value="1" :inactive-value="0"></el-switch>
                <span class="mini" style="margin-left: 8px">停用后不在发起下拉中出现</span>
              </el-form-item>
            </el-col>
            <el-col :span="16">
              <el-form-item label="备注">
                <el-input v-model="form.remark" placeholder="选填"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>

      <template #footer>
        <el-button @click="editDialog = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="doSave">保存模板</el-button>
      </template>
    </el-dialog>

    <!-- ==================== JSON 编辑弹窗 ==================== -->
    <el-dialog title="表单字段定义（JSON）" v-model="jsonDialog" width="760px">
      <el-alert type="info" :closable="false" show-icon style="margin-bottom: 10px"
                title="与上方表格是同一份数据，改完点「应用到表格」即可">
        <div>key 必须是 PDF 里真实的表单域名；defaultFrom 可用：
          partyAName / partyAPhone / partyAAddress / partyARep / partyBName / contractNo / subject /
          today / today.year / today.month / today.day</div>
      </el-alert>
      <el-input v-model="jsonText" type="textarea" :rows="18" placeholder='[{"key":"txtComA","label":"甲方公司名称","type":"text","required":true,"defaultFrom":"partyAName"}]'></el-input>
      <template #footer>
        <el-button @click="jsonDialog = false">取消</el-button>
        <el-button @click="formatJson">格式化</el-button>
        <el-button type="primary" @click="applyJson">应用到表格</el-button>
      </template>
    </el-dialog>

    <!-- ==================== 预检结果弹窗 ==================== -->
    <el-dialog title="发起前预检结果" v-model="precheckDialog" width="760px">
      <el-alert
          v-if="precheckResult"
          :type="precheckResult.ok ? 'success' : 'error'"
          :closable="false"
          show-icon
          style="margin-bottom: 12px"
          :title="precheckResult.msg">
        <div>错误 {{ precheckResult.errorCount }} 项 / 提示 {{ precheckResult.warnCount }} 项。
          「必填表单内容」报错是正常的——这里用示例入参做体检，正式发起时由操作人填写。</div>
      </el-alert>
      <el-table v-if="precheckResult" :data="precheckResult.items" border size="small">
        <el-table-column prop="name" label="检查项" width="150"></el-table-column>
        <el-table-column label="结果" width="80" align="center">
          <template #default="scope">
            <el-tag :type="levelTag(scope.row.level)" size="small">{{ levelText(scope.row.level) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="detail" label="说明" min-width="380" show-overflow-tooltip></el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="precheckDialog = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  templateAll,
  templateSave,
  templateDelete,
  detectPdfFields,
  precheckContract,
  uploadToMinio
} from "../../api/signContract";

export default {
  name: "SignContractTemplate",
  data() {
    return {
      loading: false,
      saving: false,
      detecting: false,
      uploadingPdf: false,
      keyword: "",
      templates: [],
      editDialog: false,
      form: this.emptyForm(),
      detectResult: null,
      detectSummary: "",
      jsonDialog: false,
      jsonText: "",
      precheckDialog: false,
      precheckResult: null,
      defaultFromOptions: [
        {value: "partyAName", label: "甲方名称"},
        {value: "partyAPhone", label: "甲方电话"},
        {value: "partyAAddress", label: "甲方地址"},
        {value: "partyARep", label: "甲方代表人"},
        {value: "partyBName", label: "乙方名称"},
        {value: "contractNo", label: "合同编号"},
        {value: "subject", label: "签署标题"},
        {value: "today", label: "今天(yyyy-MM-dd)"},
        {value: "today.year", label: "今天-年"},
        {value: "today.month", label: "今天-月"},
        {value: "today.day", label: "今天-日"}
      ]
    }
  },
  computed: {
    // 把「PDF 侧」的 signRule 翻译成运营真正关心的「谁先签」，避免「我方为甲方 + A_FIRST」时看错方向
    signRuleTip() {
      const r = this.form.signRule;
      const ourSide = this.form.ourRole === "partyA" ? "A" : "B";  // 我方（焱火）所占栏位
      if (r === "PARALLEL") return "实际顺序：双方同时收到链接，不限先后";
      const first = r === "A_FIRST" ? "A" : (r === "B_FIRST" ? "B" : ourSide);
      return first === ourSide
          ? "实际顺序：我方（焱火）先签 → 签完再发对方链接"
          : "实际顺序：对方先签 → 对方签完我方再签";
    },
    // 【2026-09-14】我方（焱火）在 PDF 上占的栏位 —— 由「我方角色」推导，用于把所有标签说清楚
    mySideLabel() {
      return this.form.ourRole === "partyA" ? "甲方" : "乙方";
    },
    cpSideLabel() {
      return this.mySideLabel === "甲方" ? "乙方" : "甲方";
    },
    ourRoleLabel() {
      return this.mySideLabel === "甲方" ? "我方（焱火·甲方）" : "我方（焱火·乙方）";
    },
    // 关键字是「栏位制」：甲方栏关键字谁用，取决于我方占哪一栏
    kwAUserText() {
      return this.mySideLabel === "甲方" ? "由「我方（焱火）」使用" : "由「对方（交易对手）」使用";
    },
    kwBUserText() {
      return this.mySideLabel === "乙方" ? "由「我方（焱火）」使用" : "由「对方（交易对手）」使用";
    },
    // 顶部方位速查条：一眼看清「我方落哪栏、对方落哪栏、各用哪条关键字」
    roleSummaryTitle() {
      return "本模板方位：我方（焱火）= 落「" + this.mySideLabel + "栏」 ｜ 交易对方 = 落「"
          + this.cpSideLabel + "栏」";
    },
    roleSummaryHtml() {
      const my = this.mySideLabel;
      const cp = this.cpSideLabel;
      const kwA = (this.form.signKeywordA || "").trim() || "甲方：";
      const kwB = (this.form.signKeywordB || "").trim() || "乙方：";
      const myKw = my === "甲方" ? kwA : kwB;
      const cpKw = my === "甲方" ? kwB : kwA;
      return "我方（焱火）的公章 / 授权人手写 → 落在 <b>「" + my + "栏」</b>，用关键字「"
          + myKw + "」定位；交易对方 → 落在 <b>「" + cp + "栏」</b>，用关键字「" + cpKw + "」定位。<br/>"
          + "无论方位如何，<b>本页带「我方」字样的字段恒指焱火，带「对方」字样的恒指交易对手</b>。";
    },
    filteredTemplates() {
      const k = (this.keyword || "").trim().toLowerCase();
      if (!k) return this.templates;
      return this.templates.filter(t =>
          (t.templateName || "").toLowerCase().includes(k) ||
          (t.templateCode || "").toLowerCase().includes(k));
    }
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        templateCode: "",
        templateName: "",
        bizType: "",
        pdfUrl: "",
        pdfName: "",
        partyATitle: "甲方",
        partyBTitle: "乙方",
        partyBName: "",
        partyBAutoSign: 1,
        // 【2026-09-14】对方（交易对手）签署要素：恒指对方，与「我方角色」无关
        partyASeal: 0,
        partyAHand: 1,
        partyASealSignatureId: "",
        corpSignatureId: "",
        signKeywordA: "",
        signKeywordB: "",
        // 【2026-09-14】留空 = 「我方先」（后端按 ourRole 自适应 myChar+"_FIRST"）。
        //   不要写死 B_FIRST：DB 列默认值是 A_FIRST，我方角色为甲方时两者含义相反，写死容易搞反方向。
        signRule: "",
        ourRole: "partyB",
        validDays: 30,
        state: 1,
        remark: "",
        fields: [],
        imageFields: []
      };
    },
    fieldCount(row) {
      return this.parseFields(row.fieldSchema).length;
    },
    parseFields(schema) {
      if (!schema) return [];
      try {
        const arr = typeof schema === "string" ? JSON.parse(schema) : schema;
        return Array.isArray(arr) ? arr : [];
      } catch (e) {
        return [];
      }
    },
    // ---------- 列表 ----------
    getData() {
      this.loading = true;
      templateAll().then(val => {
        this.loading = false;
        if (val && val.code === 200) {
          this.templates = val.data || [];
        } else {
          this.$message.error((val && val.message) || "获取模板列表失败");
        }
      }).catch(() => {
        this.loading = false;
        this.$message.error("获取模板列表失败");
      });
    },
    // ---------- 编辑 ----------
    openAdd() {
      this.form = this.emptyForm();
      this.detectResult = null;
      this.detectSummary = "";
      this.editDialog = true;
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        templateCode: row.templateCode,
        templateName: row.templateName,
        bizType: row.bizType,
        pdfUrl: row.pdfUrl,
        pdfName: row.pdfName,
        partyATitle: row.partyATitle || "甲方",
        partyBTitle: row.partyBTitle || "乙方",
        partyBName: row.partyBName,
        partyBAutoSign: row.partyBAutoSign === null || row.partyBAutoSign === undefined ? 1 : row.partyBAutoSign,
        // 【2026-09-14】对方（交易对手）签署要素：必须回显，否则一保存就把库里已配的对方企业章清掉
        partyASeal: row.partyASeal === 1 ? 1 : 0,
        partyAHand: row.partyAHand === null || row.partyAHand === undefined ? 1 : row.partyAHand,
        partyASealSignatureId: row.partyASealSignatureId || "",
        corpSignatureId: row.corpSignatureId || "",
        signKeywordA: row.signKeywordA || "",
        signKeywordB: row.signKeywordB || "",
        signRule: row.signRule || "",
        ourRole: row.ourRole || "partyB",
        validDays: row.validDays === null || row.validDays === undefined ? 30 : row.validDays,
        state: row.state === null || row.state === undefined ? 1 : row.state,
        remark: row.remark,
        fields: this.parseFields(row.fieldSchema).map(f => Object.assign({inPdf: null}, f)),
        imageFields: Array.isArray(row.imageFields) && row.imageFields.length
            ? row.imageFields.map(x => Object.assign({}, x))
            : this.parseFields(row.imageSchema)
      };
      this.detectResult = null;
      this.detectSummary = "";
      this.editDialog = true;
    },
    addFieldRow() {
      this.form.fields.push({key: "", label: "", type: "text", required: false, defaultFrom: "", default: "", inPdf: null});
    },
    addImageRow() {
      if (!this.form.imageFields) this.form.imageFields = [];
      this.form.imageFields.push({name: "", fieldName: "", label: "", fit: "contain", required: false});
    },
    fieldRowClass({row}) {
      return row.inPdf === false ? "row-bad" : "";
    },
    // ---------- 识别 PDF 表单域 ----------
    doDetectInForm() {
      this.detect(this.form.pdfUrl, this.form.templateCode, true);
    },
    // ---------- 上传 PDF 到 MinIO（不再手填地址） ----------
    beforePdfUpload(file) {
      const ok = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
      if (!ok) {
        this.$message.error("请选择 PDF 文件");
        return false;
      }
      this.uploadingPdf = true;
      return true;
    },
    uploadTemplatePdf({file}) {
      uploadToMinio(file, "contractTemplate").then(res => {
        this.uploadingPdf = false;
        if (res && res.code === 200 && res.data) {
          this.form.pdfUrl = res.data;
          this.$message.success("PDF 已上传到 MinIO，地址已自动回填");
        } else {
          this.$message.error((res && res.message) || "上传失败");
        }
      }).catch(() => {
        this.uploadingPdf = false;
        this.$message.error("上传失败");
      });
    },
    doDetectFor(row) {
      // 【2026-09-11 修复】列表行识别必须用该行自己的关键字（此前误用编辑弹窗里残留的 this.form 关键字，
      //   若用户打开过别的模板编辑框且改了关键字未保存，会把错误关键字覆盖到当前行的识别结果上）
      this.detect(row.pdfUrl, row.templateCode, false, row.signKeywordA, row.signKeywordB);
    },
    detect(pdfUrl, templateCode, applyToForm, keywordA, keywordB) {
      if (!pdfUrl) {
        this.$message.warning("请先填写 PDF 地址");
        return;
      }
      this.detecting = true;
      detectPdfFields(pdfUrl, templateCode || undefined,
          keywordA !== undefined ? keywordA : ((this.form && this.form.signKeywordA) || undefined),
          keywordB !== undefined ? keywordB : ((this.form && this.form.signKeywordB) || undefined))
          .then(val => {
            this.detecting = false;
            if (!(val && val.code === 200 && val.data)) {
              this.$message.error((val && val.message) || "识别失败");
              return;
            }
            const d = val.data;
            this.detectResult = d;
            const pdfKeys = (d.fields || []).filter(f => f.fillable).map(f => f.name);
            if (applyToForm) {
              const keep = [];
              const add = [];
              const existing = {};
              this.form.fields.forEach(r => {
                if (r.key) existing[r.key] = true;
              });
              // ① 已有行：标记是否命中当前 PDF（不删除，避免误删其它版式的字段）
              this.form.fields.forEach(r => {
                r.inPdf = r.key ? pdfKeys.includes(r.key) : null;
                if (r.inPdf) keep.push(r.key);
              });
              // ② PDF 里有、表格里没有的 → 追加（用建议的标签与自动带出）
              (d.suggestedSchema || []).forEach(s => {
                if (!existing[s.key]) {
                  this.form.fields.push(Object.assign({default: "", inPdf: true}, s));
                  add.push(s.key);
                }
              });
              const unconfigured = pdfKeys.filter(k => !this.form.fields.some(r => r.key === k));
              // 图片表单域：以本次识别结果为准（PDF 里有几个图片域 → 发起时就出几个上传位）
              const imgSlots = d.suggestedImageSlots || [];
              this.form.imageFields = imgSlots.map(s => Object.assign({}, s));
              this.detectSummary = "已保留 " + keep.length + " 个已配字段，自动新增 " + add.length + " 个"
                  + "；图片上传位 " + imgSlots.length + " 个"
                  + (unconfigured.length ? "；仍有 " + unconfigured.length + " 个 PDF 域未配置" : "")
                  + (this.form.fields.some(r => r.inPdf === false)
                      ? "；标红「不存在」的行请核对（可能是另一版 PDF 的字段）" : "");
            } else {
              this.detectSummary = "";
            }
            this.$message.success("识别完成：" + (d.pages || 0) + " 页 / " + pdfKeys.length + " 个可填表单域 / "
                + ((d.suggestedImageSlots || []).length) + " 个图片域");
          })
          .catch(() => {
            this.detecting = false;
            this.$message.error("识别失败");
          });
    },
    // ---------- JSON 视图 ----------
    openJsonDialog() {
      this.jsonText = JSON.stringify(this.cleanFields(), null, 2);
      this.jsonDialog = true;
    },
    formatJson() {
      try {
        this.jsonText = JSON.stringify(JSON.parse(this.jsonText), null, 2);
      } catch (e) {
        this.$message.error("JSON 格式有误：" + e.message);
      }
    },
    applyJson() {
      try {
        const arr = JSON.parse(this.jsonText);
        if (!Array.isArray(arr)) {
          this.$message.error("必须是 JSON 数组");
          return;
        }
        this.form.fields = arr.map(f => Object.assign({inPdf: null, type: "text", required: false}, f));
        this.jsonDialog = false;
        this.$message.success("已应用到表格（保存后生效）");
      } catch (e) {
        this.$message.error("JSON 解析失败：" + e.message);
      }
    },
    cleanFields() {
      return (this.form.fields || [])
          .filter(f => f && f.key && String(f.key).trim())
          .map(f => {
            const o = {key: String(f.key).trim()};
            if (f.label) o.label = f.label;
            if (f.type) o.type = f.type;
            if (f.required === true) o.required = true;
            if (f.defaultFrom) o.defaultFrom = f.defaultFrom;
            if (f.default !== undefined && f.default !== null && f.default !== "") o.default = f.default;
            return o;
          });
    },
    // 图片表单域：只保留必要字段（name 必填；fieldName 缺省=name；坐标有则带上）
    cleanImageFields() {
      return (this.form.imageFields || [])
          .filter(f => f && f.name && String(f.name).trim())
          .map(f => {
            const name = String(f.name).trim();
            const o = {name: name, fieldName: String(f.fieldName || name).trim()};
            if (f.label) o.label = f.label;
            o.fit = f.fit || "contain";
            if (f.required === true) o.required = true;
            ["page", "x", "y", "w", "h"].forEach(k => {
              if (f[k] !== undefined && f[k] !== null && f[k] !== "") o[k] = f[k];
            });
            return o;
          });
    },
    // ---------- 保存 / 删除 ----------
    doSave() {
      if (!this.form.templateCode || !this.form.templateName) {
        this.$message.warning("模板编码与名称必填");
        return;
      }
      const bad = (this.form.fields || []).filter(f => f.key && f.inPdf === false);
      const payload = {
        id: this.form.id,
        templateCode: this.form.templateCode,
        templateName: this.form.templateName,
        bizType: this.form.bizType,
        pdfUrl: this.form.pdfUrl,
        pdfName: this.form.pdfName,
        partyATitle: this.form.partyATitle,
        partyBTitle: this.form.partyBTitle,
        partyBName: this.form.partyBName,
        partyBAutoSign: this.form.partyBAutoSign,
        // 【2026-09-14】对方（交易对手）签署要素
        partyASeal: this.form.partyASeal,
        partyAHand: this.form.partyAHand,
        partyASealSignatureId: this.form.partyASealSignatureId,
        corpSignatureId: this.form.corpSignatureId,
        signKeywordA: this.form.signKeywordA,
        signKeywordB: this.form.signKeywordB,
        signRule: this.form.signRule,
        ourRole: this.form.ourRole,
        validDays: this.form.validDays,
        state: this.form.state,
        remark: this.form.remark,
        fieldSchema: JSON.stringify(this.cleanFields()),
        imageSchema: JSON.stringify(this.cleanImageFields())
      };
      const submit = () => {
        this.saving = true;
        templateSave(payload).then(val => {
          this.saving = false;
          if (val && val.code === 200 && val.data && val.data.code === "1") {
            this.$message.success((val.data && val.data.msg) || "保存成功");
            this.editDialog = false;
            this.getData();
          } else {
            this.$message.error((val && (val.message || (val.data && val.data.msg))) || "保存失败");
          }
        }).catch(() => {
          this.saving = false;
          this.$message.error("保存失败");
        });
      };
      if (bad.length) {
        this.$confirm("这些字段在 PDF 里找不到，保存后它们不会有值：" + bad.map(f => f.key).join("、") + "。确定保存吗？",
            "确认保存", {type: "warning"}).then(submit).catch(() => {});
        return;
      }
      submit();
    },
    doDelete(row) {
      this.$confirm("确定删除模板「" + row.templateName + "」吗？历史合同不受影响，但该模板将不能再发起新合同。",
          "删除模板", {type: "warning", confirmButtonText: "确定删除"}).then(() => {
        templateDelete(row.id).then(val => {
          if (val && val.code === 200 && val.data && val.data.code === "1") {
            this.$message.success("已删除");
            this.getData();
          } else {
            this.$message.error((val && (val.message || (val.data && val.data.msg))) || "删除失败");
          }
        });
      }).catch(() => {});
    },
    // ---------- 预检 ----------
    doPrecheck(row) {
      const fields = this.parseFields(row.fieldSchema);
      // 用示例入参体检：重点看 PDF / 字段对齐 / 落章关键字 / 印章四项
      const formData = {};
      fields.forEach(f => {
        if (f && f.key) formData[f.key] = "预检示例值";
      });
      precheckContract({
        templateCode: row.templateCode,
        partyAName: "（预检示例）甲方名称",
        partyAPhone: "13800000000",
        partyBName: row.partyBName,
        formData
      }).then(val => {
        if (val && val.code === 200 && val.data) {
          this.precheckResult = val.data;
          this.precheckDialog = true;
        } else {
          this.$message.error((val && val.message) || "预检失败");
        }
      }).catch(() => this.$message.error("预检失败"));
    },
    // ---------- 工具 ----------
    levelTag(level) {
      if (level === "error") return "danger";
      if (level === "warn") return "warning";
      return "success";
    },
    levelText(level) {
      if (level === "error") return "不通过";
      if (level === "warn") return "提示";
      return "通过";
    }
  },
  created() {
    this.getData();
  }
}
</script>

<style scoped>
.filter-bar {
  padding-bottom: 18px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.mini {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.field-tip {
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
  color: #909399;
}

.edit-body {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;
}

.row-tools {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.field-table-wrap {
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.op-danger {
  color: #f56c6c;
}

:deep(.row-bad) {
  background: #fef0f0;
}
</style>
